import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown, ArrowUpRight } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1638398416806-d85f89f90120?crop=entropy&cs=srgb&fm=jpg&q=85";

const line = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.4 + i * 0.16, duration: 1.0, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fade = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.9 + i * 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={HERO_BG}
          alt="Freight truck crossing the Sonoran desert at dusk"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#1E1511]/40 via-[#1E1511]/55 to-[#1E1511]" />
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(circle at 50% 18%, rgba(232,157,75,0.20) 0%, rgba(30,21,17,0) 65%)" }}
      />

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pb-16 pt-40"
      >
        <motion.div
          custom={0}
          variants={fade}
          initial="hidden"
          animate="visible"
          data-testid="hero-badge"
          className="font-mono text-xs tracking-[0.3em] uppercase text-[#E89D4B] mb-8 flex items-center gap-3"
        >
          <span className="w-10 h-px bg-[#C85A32]" />
          01 // Phoenix, Arizona — B2B Freight
        </motion.div>

        <h1 className="font-display font-bold tracking-tight leading-[0.98] text-[#F7F2EB] text-5xl sm:text-7xl lg:text-[6.5rem]">
          <span className="block overflow-hidden pb-1">
            <motion.span data-testid="hero-headline-line-1" custom={0} variants={line} initial="hidden" animate="visible" className="block">
              Precision Hauling.
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span data-testid="hero-headline-line-2" custom={1} variants={line} initial="hidden" animate="visible" className="block italic text-[#E89D4B]">
              Desert Reliability.
            </motion.span>
          </span>
        </h1>

        <motion.p
          custom={1}
          variants={fade}
          initial="hidden"
          animate="visible"
          data-testid="hero-subheading"
          className="mt-8 max-w-xl text-base sm:text-lg text-[#E6DACB]/90 leading-relaxed"
        >
          Boutique dock-to-door freight across Greater Phoenix and all Arizona counties —
          one 26-ft box truck, a 3,000 lb hydraulic lift gate, and a dispatch that answers.
        </motion.p>

        <motion.div
          custom={2}
          variants={fade}
          initial="hidden"
          animate="visible"
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#quote"
            data-testid="hero-quote-cta"
            className="group flex items-center gap-2 bg-[#C85A32] text-[#F7F2EB] font-mono text-xs tracking-[0.18em] uppercase rounded-full px-8 py-4 hover:bg-[#E89D4B] hover:text-[#1E1511] transition-colors duration-300"
          >
            Request Freight Quote
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="tel:+16026694590"
            data-testid="hero-call-cta"
            className="flex items-center gap-2 border border-[#E6DACB]/30 text-[#E6DACB] font-mono text-xs tracking-[0.18em] uppercase rounded-full px-8 py-4 hover:border-[#E89D4B] hover:text-[#E89D4B] transition-colors duration-300"
          >
            <Phone size={14} />
            Call Dispatch
          </a>
        </motion.div>

        <motion.div
          custom={3}
          variants={fade}
          initial="hidden"
          animate="visible"
          className="mt-14 flex flex-wrap items-center justify-between gap-6 border-t border-[#E6DACB]/15 pt-6"
        >
          <div className="flex flex-wrap gap-x-10 gap-y-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[#E6DACB]/60">
            <span data-testid="hero-stat-truck">26-FT Box Truck</span>
            <span data-testid="hero-stat-gate">3,000 LB Lift Gate</span>
            <span data-testid="hero-stat-sameday">Same-Day Dispatch</span>
          </div>
          <div data-testid="hero-scroll-indicator" className="flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[#C85A32]">
            Scroll
            <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
              <ArrowDown size={14} />
            </motion.span>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};
